/* eslint-disable camelcase */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiClient from 'src/api/api-client';
import { getBodURI, detailBodURI } from '../api';
import { setValue } from './MessageSlice';
import { setDefaultValue } from './BodItemSlice';

// First, create the thunk
export const fetchBods = createAsyncThunk(
  'bods/fetchBods',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiClient
        .handleRequest({
          method: 'get',
          url: `${getBodURI}`,
          params: { ...params, get_all: true },
        })
        .then(res => res.data);
      return response.data;
    } catch (err) {
      dispatch(setValue({ show: true, body: err.message }));
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const searchBods = createAsyncThunk(
  'bods/searchBods',
  async (data, { rejectWithValue, dispatch, getState }) => {
    const response = await apiClient
      .handleRequest({
        method: 'get',
        url: `${getBodURI}`,
        params: data,
      })
      .then(res => res.data);
    return true;
  },
);

export const upsertBod = createAsyncThunk(
  'bods/upsertBod',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      await apiClient.handleRequest({
        method: 'post',
        url: `${detailBodURI(data.id)}`,
        data: data.data,
      });
      dispatch(fetchBods({ page: 1 }));
      return true;
    } catch (err) {
      dispatch(setValue({ show: true, body: err.message }));
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateBodStatus = createAsyncThunk(
  'bods/updateBodStatus',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      await apiClient.handleRequest({
        method: 'post',
        url: `${detailBodURI(data.id)}`,
        data: { ...data, status: data.status === 'inactive' ? 'active' : 'inactive' },
      });
      let currentPage = 1;
      if (data.id) {
        const state = getState();
        currentPage = state.bod.currentPage;
      }
      dispatch(fetchBods({ page: currentPage }));
      return true;
    } catch (err) {
      dispatch(setValue({ show: true, body: err.message }));
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const detailBods = createAsyncThunk(
  'bods/detailBods',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await apiClient
        .handleRequest({
          method: 'get',
          url: `${detailBodURI(data.id)}`,
        })
        .then(res => res.data);
      dispatch(setDefaultValue(response.data[0]));
      return true;
    } catch (err) {
      dispatch(setValue({ show: true, body: err.message }));
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteBods = createAsyncThunk(
  'bods/upsertBod',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      await apiClient
        .handleRequest({
          method: 'delete',
          url: `${detailBodURI(data.id)}`,
        })
        .then(res => res.data);
      dispatch(fetchBods({ page: 1 }));
      return true;
    } catch (err) {
      dispatch(setValue({ show: true, body: err.message }));
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  },
);

// Then, handle actions in your reducers:
const bodSlice = createSlice({
  name: 'bods',
  initialState: {
    entities: [],
    loading: 'idle',
    saving: 'idle',
    currentRequestId: undefined,
    error: null,
    currentPage: 1,
    totalPage: 1,
    total: 0,
  },
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
  },
  extraReducers: {
    [fetchBods.pending]: (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.currentRequestId = action.meta.requestId;
      }
    },
    // Add reducers for additional action types here, and handle loading state as needed
    [fetchBods.fulfilled]: (state, action) => {
      const { requestId } = action.meta;
      if (state.loading === 'pending' && state.currentRequestId === requestId) {
        const { data, last_page, current_page, total } = action.payload;
        state.loading = 'idle';
        state.entities = data;
        state.currentPage = current_page;
        state.totalPage = last_page;
        state.currentRequestId = undefined;
        state.total = total;
      }
    },
    [fetchBods.rejected]: (state, action) => {
      const { requestId } = action.meta;
      if (state.loading === 'pending' && state.currentRequestId === requestId) {
        state.loading = 'idle';
        state.error = action.error;
        state.currentRequestId = undefined;
      }
    },

    [upsertBod.pending]: (state, action) => {
      if (state.saving === 'idle') {
        state.saving = 'pending';
        state.currentRequestId = action.meta.requestId;
      }
    },
    [upsertBod.fulfilled]: (state, action) => {
      state.saving = 'idle';
    },
    [upsertBod.rejected]: (state, action) => {
      const { requestId } = action.meta;
      if (state.saving === 'pending' && state.currentRequestId === requestId) {
        state.saving = 'idle';
        state.error = action.error;
        state.currentRequestId = undefined;
      }
    },
    [searchBods.fulfilled]: (state, action) => {
      const { data, last_page, current_page, total } = action.payload;
      state.loading = 'idle';
      state.entities = data;
      state.currentPage = current_page;
      state.totalPage = last_page;
      state.currentRequestId = undefined;
      state.total = total;
    },
  },
});

export default bodSlice.reducer;
